import { useLoaderData, useNavigate } from 'react-router';
import {
  useState,
  useContext,
  type SyntheticEvent,
  type ChangeEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { type QuestionType } from '@ikea-customer-self-serve/contact-form';
import services from '../services';
import {
  type Question,
  type Answer,
  type FileAnswer,
} from '../services/additionalInfo/model';
import { showError } from '../reducers/error/errorReducer';
import {
  getAnswersPayload,
  hasExceededCharactersLength,
  hasMissingAnswer,
  hasMissingConsent,
} from '../helpers/questionsHelper';
import { useStateDispatch, useStateSelector } from '../store/';
import {
  addPersistingQueryParamsToPath,
  getPagePath,
} from '../helpers/urlHelper';
import { ERRORS } from '../services/errors';
import { sendEventToParent } from '../eventPublisher';
import type { QuestionsPageLoaderData } from '../routers/loaders/questionsPageLoader';
import { ViewContext } from '../context';

interface UseQuestionsState {
  questions: Question[];
  questionsToken?: string;
  answers: Answer[];
  shouldValidate: boolean;
  questionsAnswered: boolean;
  hasFileUploadConsent: boolean;
  showErrorOnSubmit: boolean;
  submitCount: number;
  touchedFields: Set<number>;
  isBusy: boolean;
}

interface UseQuestionActions {
  changeInputHandler: (
    e: ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    > | null,
    index: number,
    questionType: QuestionType,
    files?: FileAnswer[],
  ) => void;
  blurHandler: (index: number) => void;
  submitHandler: (e: SyntheticEvent) => Promise<void>;
}

const initAnswersState = (
  questions: Question[],
  questionsToken?: string,
  questionsAnswered?: boolean,
): Answer[] => {
  if (questionsToken && !questionsAnswered) {
    const newAnswers: Answer[] = [...new Array(questions.length).keys()].map(
      (index) => {
        return {
          questionId: questions[index].id,
          answer: [],
          questionType: questions[index].type,
        };
      },
    );
    return newAnswers;
  } else {
    return [];
  }
};

export const useQuestions = (
  hasFileUploadConsent: boolean,
): [UseQuestionsState, UseQuestionActions] => {
  const { questions, questionsToken, questionsAnswered } =
    useLoaderData<QuestionsPageLoaderData>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useStateDispatch();
  const view = useContext(ViewContext);
  const [showErrorOnSubmit, setShowErrorOnSubmit] = useState(false);
  const [answers, setAnswers] = useState<Answer[]>(() =>
    initAnswersState(questions, questionsToken, questionsAnswered),
  );
  const [isBusy, setIsBusy] = useState(false);
  const [shouldValidate, setShouldValidate] = useState<boolean>(false);
  const [submitCount, setSubmitCount] = useState(0);
  const [touchedFields, setTouchedFields] = useState<Set<number>>(new Set());

  const orderNumber = useStateSelector((state) => state.app.booking.orderNo);

  const changeInputHandler = (
    e: ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    > | null,
    index: number,
    questionType: QuestionType,
    files?: FileAnswer[],
  ): void => {
    const checked =
      e?.target instanceof HTMLInputElement ? e.target.checked : undefined;
    const value = files ? files : e?.target.value;

    setAnswers((prevAnswers) => {
      const newAnswers = [...prevAnswers];
      switch (questionType) {
        case 'TEXT': {
          newAnswers[index] = {
            ...newAnswers[index],
            answer: [value as string],
          };
          return newAnswers;
        }
        case 'CHECKBOX': {
          const currentAnswers = newAnswers[index].answer as string[];
          const updatedAnswer = checked
            ? [...currentAnswers, value as string]
            : currentAnswers.filter((v: string) => v !== value);
          newAnswers[index] = {
            ...newAnswers[index],
            answer: updatedAnswer,
          };
          return newAnswers;
        }
        case 'RADIO': {
          newAnswers[index] = {
            ...newAnswers[index],
            answer: [value as string],
          };
          return newAnswers;
        }
        case 'FILE': {
          newAnswers[index] = {
            ...newAnswers[index],
            answer: value as FileAnswer[],
          };
          return newAnswers;
        }
        default: {
          return newAnswers;
        }
      }
    });
  };

  const blurHandler = (index: number): void => {
    setTouchedFields((prev) => new Set(prev).add(index));
  };

  const showErrorModal = (status?: string): void => {
    const errorActionPayload = status
      ? {
          title: t(`components.error.questions.status.${status}.title`),
          description: t(
            `components.error.questions.status.${status}.description`,
          ),
          button: t('components.error.close'),
        }
      : {
          title: t('components.error.questions.title'),
          description: t('components.error.questions.description'),
          button: t('components.error.close'),
        };
    dispatch(showError(errorActionPayload));
  };

  const submitHandler = async (e: SyntheticEvent): Promise<void> => {
    e.preventDefault();
    setShowErrorOnSubmit(false);

    if (!questionsToken) {
      showErrorModal();
      return;
    }

    setIsBusy(true);

    try {
      if (
        hasMissingAnswer(answers) ||
        hasExceededCharactersLength(answers) ||
        hasMissingConsent(answers, hasFileUploadConsent)
      ) {
        setShowErrorOnSubmit(true);
        setSubmitCount((prev) => prev + 1);
        setShouldValidate(true);
      } else {
        const answersPayload = getAnswersPayload(answers, hasFileUploadConsent);
        const { error } = await services.additionalInfo.postAnswers(
          questionsToken,
          answersPayload,
          orderNumber,
        );

        const isErrorWithStatus = (
          value: unknown,
        ): value is { status: number; data: unknown } => {
          return (
            typeof value === 'object' &&
            value !== null &&
            'status' in value &&
            'data' in value
          );
        };

        const isHandledHttpError =
          isErrorWithStatus(error) &&
          [400, 403, 500, 404].indexOf(error.status) > -1;

        if (isHandledHttpError || error === ERRORS.SERVICE_NOT_AVAILABLE) {
          const status = isErrorWithStatus(error) ? error.data : undefined;
          if (
            typeof status === 'string' &&
            (status === 'QUESTION_ALREADY_ANSWERED' ||
              status === 'APPOINTMENT_ALREADY_CANCELLED')
          ) {
            showErrorModal(status);
          } else {
            showErrorModal();
          }
        } else {
          sendEventToParent('BOKA/QUESTIONS_ANSWERED', view);
          const path = getPagePath('questions-confirmed', view);
          navigate(addPersistingQueryParamsToPath(path));
        }
      }
    } finally {
      setIsBusy(false);
    }
  };

  const state = {
    questions: [...questions],
    questionsToken,
    answers,
    shouldValidate,
    questionsAnswered,
    hasFileUploadConsent,
    showErrorOnSubmit,
    submitCount,
    touchedFields,
    isBusy,
  };
  const actions = {
    changeInputHandler,
    blurHandler,
    submitHandler,
  };
  return [state, actions];
};
